/**
 * draftStepFieldConfig — exhaustive mapping from every DraftStep to its
 * Dimension Input field specification.
 *
 * AutoCAD parity rules (DYNMODE bit 2 path):
 *  - "none"           → no Dimension Input shown (first-point, dialog, select,
 *                       content, confirm, ready steps).
 *  - "length"         → single numeric field: distance along cursor direction.
 *  - "radius"         → single numeric field: arc/circle radius.
 *  - "diameter"       → single numeric field: circle diameter.
 *  - "rotation"       → single numeric field: angle in degrees.
 *  - "angle"          → single numeric field: subtended angle in degrees (e.g.
 *                       polar array fill angle, dim angular arc).
 *  - "scalar"         → single numeric field: a UNITLESS multiplier / factor
 *                       (e.g. hatch pattern scale). Commits a bare number, not
 *                       a polar `@d<a` distance token like "length".
 *  - "width-height"   → two numeric fields: primary = width (or dX), secondary
 *                       = height (or dY). Used for rectangle second corner,
 *                       mtext second corner, array spacing steps.
 *  - "distance-angle" → two numeric fields: primary = distance, secondary =
 *                       angle (degrees). Default UI for LINE/PLINE/ARC-end
 *                       continuation — AutoCAD shows both fields editable
 *                       with TAB toggling lock between them. Commits as
 *                       relative-polar @{d}<{a}.
 *
 * The `satisfies Record<DraftStep, DimFieldSpec>` annotation ensures the
 * TypeScript compiler catches any DraftStep that is missing a mapping at
 * build time. Add new steps here whenever draftingHelpers.ts is extended.
 */

import type { DraftStep } from "../scene/draftingHelpers";

// ─── Types ────────────────────────────────────────────────────────────────────

export type DimFieldKind =
  | "none"
  | "length"
  | "radius"
  | "diameter"
  | "rotation"
  | "angle"
  | "scalar"
  | "width-height"
  | "distance-angle";

export type DimFieldSpec = {
  kind: DimFieldKind;
  /** i18n key for the primary field label (empty string when kind="none"). */
  primaryLabelKey: string;
  /**
   * i18n key for the secondary label.  Only present for "width-height" or
   * any future two-field spec where the secondary label differs.
   */
  secondaryLabelKey?: string;
};

// ─── Sentinel ─────────────────────────────────────────────────────────────────

/** Sentinel used for every step where AutoCAD shows no Dimension Input. */
export const DIM_FIELD_NONE: DimFieldSpec = {
  kind: "none",
  primaryLabelKey: "",
};

// ─── Helpers for brevity ──────────────────────────────────────────────────────

function len(): DimFieldSpec {
  return { kind: "length", primaryLabelKey: "dynin.field.length" };
}
function rad(): DimFieldSpec {
  return { kind: "radius", primaryLabelKey: "dynin.field.radius" };
}
function dia(): DimFieldSpec {
  return { kind: "diameter", primaryLabelKey: "dynin.field.diameter" };
}
function rot(): DimFieldSpec {
  return { kind: "rotation", primaryLabelKey: "dynin.field.rotation" };
}
function ang(): DimFieldSpec {
  return { kind: "angle", primaryLabelKey: "dynin.field.angle" };
}
function scl(): DimFieldSpec {
  // Plain UNITLESS number (a multiplier / factor — e.g. a hatch pattern
  // scale). Unlike `length`, it must NOT be turned into a polar `@d<a`
  // distance token by the DI builder — it commits a bare scalar like the
  // radius/rotation/angle kinds.
  return { kind: "scalar", primaryLabelKey: "dynin.field.scale" };
}
function wh(): DimFieldSpec {
  return {
    kind: "width-height",
    primaryLabelKey: "dynin.field.width",
    secondaryLabelKey: "dynin.field.height",
  };
}
function distAng(): DimFieldSpec {
  return {
    kind: "distance-angle",
    primaryLabelKey: "dynin.field.distance",
    secondaryLabelKey: "dynin.field.angle",
  };
}
const N = DIM_FIELD_NONE;

// ─── Exhaustive mapping ───────────────────────────────────────────────────────

/**
 * Each DraftStep is explicitly listed.  The `satisfies` annotation is the
 * compile-time coverage gate: if a new step is added to `DraftStep` but
 * not listed here the build will fail with a type error.
 */
export const DRAFT_STEP_FIELDS = {
  // ── LINE ──────────────────────────────────────────────────────────────────
  "line-first-point": N,
  // AutoCAD parity: 2nd+ point of LINE shows two fields by default — distance
  // and angle (relative-polar). TAB toggles which field is locked.
  "line-next-point": distAng(),

  // ── POINT ─────────────────────────────────────────────────────────────────
  // Single-click commit. AutoCAD shows no Dimension Input field — the
  // only input is the click coordinate (or typed absolute coords).
  "point-location": N,

  // ── BASE / БАЗА ($INSBASE) ──────────────────────────────────────────────────
  // Single point pick; like POINT, AutoCAD shows no Dimension Input field.
  "insbase-point": N,

  // ── CIRCLE ────────────────────────────────────────────────────────────────
  "circle-center": N,
  "circle-radius": rad(),
  "circle-diameter": dia(),
  "circle-ready": N,

  // ── ARC ───────────────────────────────────────────────────────────────────
  "arc-start": N,
  // 2nd point of a 3-point arc — determines direction; distance is not the
  // primary UI value shown by AutoCAD (it shows coordinates, not a length).
  "arc-second": N,
  // AutoCAD parity: 3rd point of ARC shows distance + angle (relative-polar).
  "arc-end": distAng(),
  "arc-ready": N,

  // ── ELLIPSE — center mode (Phase D4) ──────────────────────────────────────
  // First click is just a point pick (no DI in AutoCAD).
  // Major-end click: AutoCAD shows distance/angle from the center, matching
  // the LINE-second-point and ARC-end behaviour.
  // Minor-distance click/typed: single length field — typed value is the
  // perpendicular minor-axis distance from center.
  "ellipse-center": N,
  "ellipse-major-end": distAng(),
  "ellipse-minor-distance": len(),

  // ── POLYLINE ──────────────────────────────────────────────────────────────
  "polyline-first-point": N,
  // AutoCAD parity: PLINE shows distance + angle for each subsequent vertex.
  "polyline-next-point": distAng(),

  // ── RECTANGLE ─────────────────────────────────────────────────────────────
  "rectangle-first-corner": N,
  "rectangle-second-corner": wh(),
  "rectangle-ready": N,

  // ── TEXT ──────────────────────────────────────────────────────────────────
  "text-insertion-point": N,
  "text-height": len(),
  "text-rotation": rot(),
  "text-content": N,

  // ── MTEXT ─────────────────────────────────────────────────────────────────
  "mtext-first-corner": N,
  "mtext-second-corner": wh(),
  "mtext-height": len(),
  "mtext-content": N,

  // ── BLOCK command (create-block flow) ─────────────────────────────────────
  "block-name": N,
  "block-insertion-point": N,
  "block-scale": len(),
  "block-rotation": rot(),
  "block-ready": N,

  // ── INSERT (block select-objects / base-point / dialog) ───────────────────
  "block-select-objects": N,
  "block-base-point": N,
  "block-dialog": N,

  // ── INSERT specify-on-screen steps ────────────────────────────────────────
  "insert-dialog": N,
  "insert-specify-point": N,
  "insert-specify-scale": len(),
  "insert-specify-rotation": rot(),
  "insert-collect-attribs": N,

  // ── ARRAYRECT ─────────────────────────────────────────────────────────────
  "arrayrect-select": N,
  "arrayrect-base-point": N,
  "arrayrect-count": N,
  // Spacing = column spacing × row spacing — two numeric fields.
  "arrayrect-spacing": wh(),

  // ── ARRAYPOLAR ────────────────────────────────────────────────────────────
  "arraypolar-select": N,
  "arraypolar-center": N,
  "arraypolar-count": N,
  // Fill angle: total sweep angle in degrees.
  "arraypolar-fill": ang(),

  // ── ARRAYPATH ─────────────────────────────────────────────────────────────
  "arraypath-select": N,
  "arraypath-path": N,
  "arraypath-method": N,
  "arraypath-params": N,

  // ── ARRAYEDIT ─────────────────────────────────────────────────────────────
  "arrayedit-select": N,

  // ── HATCHEDIT ─────────────────────────────────────────────────────────────
  // Select step: entity pick / typed id — no Dimension Input field.
  // Pattern: text content (pattern name) — no DI field.
  // Scale: UNITLESS multiplier — a `scalar` field, NOT `length`. A length
  //   field would emit a polar `@d<a` token (parseFloat → NaN → re-prompt);
  //   scalar commits the bare number the scale handler expects.
  // Angle: rotation in degrees (genuinely rotational).
  "hatchedit-select": N,
  "hatchedit-pattern": N,
  "hatchedit-scale": scl(),
  "hatchedit-angle": rot(),

  // ── BEDIT / BCLOSE / BSAVE ────────────────────────────────────────────────
  "bedit-prompt-name": N,
  "bedit-confirm-create": N,
  "bclose-confirm": N,

  // ── ATTDEF / DDATTE / ATTEDIT / EATTEDIT ──────────────────────────────────
  "attdef-dialog": N,
  "ddatte-select": N,
  "ddatte-dialog": N,
  "eattedit-select": N,
  "eattedit-dialog": N,

  // ── HATCH ─────────────────────────────────────────────────────────────────
  "hatch-pattern": N,
  "hatch-first-corner": N,
  "hatch-second-corner": wh(),
  "hatch-ready": N,
  // HATCH internal-pick mode (Phase E):
  //   `hatch-mode-prompt` is a keyword prompt (no DI field).
  //   The two pick steps are click-only — no DI field shown either.
  //   AutoCAD parity: no overlay during seed-pick (cursor crosshair only).
  "hatch-mode-prompt": N,
  "hatch-pick-internal-point": N,
  "hatch-pick-additional": N,

  // ── DIMENSION (DIMLINEAR) ──────────────────────────────────────────────────
  "dimension-first-point": N,
  "dimension-second-point": N,
  // Dim-line placement: perpendicular offset distance from the measured line.
  "dimension-line-point": len(),
  "dimension-ready": N,

  // ── DIMALIGNED ────────────────────────────────────────────────────────────
  "dimaligned-first-point": N,
  "dimaligned-second-point": N,
  "dimaligned-line-point": len(),

  // ── DIMANGULAR ────────────────────────────────────────────────────────────
  "dimangular-first-line": N,
  "dimangular-second-line": N,
  // Arc placement point for the angular dimension arc.
  "dimangular-arc-point": ang(),

  // ── DIM3POINT ─────────────────────────────────────────────────────────────
  "dim3point-vertex": N,
  "dim3point-first-arm": N,
  "dim3point-second-arm": N,
  "dim3point-arc-point": ang(),

  // ── DIMRADIUS ─────────────────────────────────────────────────────────────
  "dimradius-select-arc": N,
  // The dimension line for a radius dimension is a length from center.
  "dimradius-dim-line": len(),

  // ── DIMDIAMETER ───────────────────────────────────────────────────────────
  "dimdiameter-select-arc": N,
  "dimdiameter-dim-line": len(),

  // ── DIMORDINATE ───────────────────────────────────────────────────────────
  "dimordinate-feature-point": N,
  "dimordinate-leader-end": len(),

  // ── MLEADER ───────────────────────────────────────────────────────────────
  "mleader-arrow-point": N,
  // Landing: horizontal leader landing length.
  "mleader-landing-point": len(),
  "mleader-content": N,

  // ── MLEADEREDIT ───────────────────────────────────────────────────────────
  "mleaderedit-select": N,
  "mleaderedit-options": N,
  "mleaderedit-add-leader-arrow": N,
  "mleaderedit-remove-pick": N,

  // ── REVCLOUD ──────────────────────────────────────────────────────────────
  "revcloud-first-corner": N,
  "revcloud-second-corner": wh(),
  "revcloud-ready": N,
  // REVCLOUD-MODES — keyword sub-prompts (text-only, no DI overlay) +
  // Polygonal / Freehand point loops (no dimension fields — the cloud
  // chain has no single length/angle pair) + Object pick steps.
  "revcloud-arc-length": N,
  "revcloud-style": N,
  "revcloud-polygonal-next": N,
  "revcloud-freehand-path": N,
  "revcloud-object-pick": N,
  "revcloud-object-reverse": N,

  // ── WIPEOUT ───────────────────────────────────────────────────────────────
  // First-pass rectangular flow mirrors REVCLOUD/MVIEW: width-height field
  // on the second corner, no DI overlay on first-corner / ready steps.
  "wipeout-first-corner": N,
  "wipeout-second-corner": wh(),
  "wipeout-ready": N,

  // ── MVIEW (viewport) ──────────────────────────────────────────────────────
  "viewport-first-corner": N,
  "viewport-second-corner": wh(),
  "viewport-ready": N,

  // ── VPCLIP-INTERACTIVE (@codex round-5 P1) ────────────────────────────────
  // "Select viewport to clip:" — a select-only step, no DI (mirrors
  // "block-select-objects"). The two corner steps mirror MVIEW/RECTANGLE's
  // own rectangular-pick pair exactly: first corner is a bare point (no DI),
  // second corner shows the width/height field.
  "vpclip-select-viewport": N,
  "vpclip-first-corner": N,
  "vpclip-second-corner": wh(),

  // ── MOVE ──────────────────────────────────────────────────────────────────
  "move-select": N,
  "move-base-point": N,
  // Destination = relative displacement → length field (AutoCAD shows
  // distance tooltip for the second point of MOVE).
  "move-destination": len(),

  // ── COPY ──────────────────────────────────────────────────────────────────
  "copy-select": N,
  "copy-base-point": N,
  "copy-destination": len(),

  // ── MIRROR ────────────────────────────────────────────────────────────────
  "mirror-select": N,
  "mirror-first-point": N,
  "mirror-second-point": len(),

  // ── ROTATE ────────────────────────────────────────────────────────────────
  "rotate-select": N,
  "rotate-base-point": N,
  // Rotation angle entered directly.
  "rotate-angle": rot(),

  // ── SCALE ─────────────────────────────────────────────────────────────────
  "scale-select": N,
  "scale-base-point": N,
  // Scale factor is a dimensionless number but shows in the length-style
  // field (AutoCAD uses a single numeric dim input field for scale factor).
  "scale-factor": len(),

  // ── OFFSET ────────────────────────────────────────────────────────────────
  // OFFSET first asks for a distance (typed value), then a side pick.
  "offset-distance": len(),
  "offset-select-entity": N,
  "offset-side": N,

  // ── STRETCH ───────────────────────────────────────────────────────────────
  "stretch-select-first": N,
  "stretch-select-second": N,
  "stretch-base": N,
  "stretch-destination": len(),

  // ── TRIM / EXTEND ─────────────────────────────────────────────────────────
  "trim-select-edges": N,
  "trim-pick-entity": N,
  "extend-select-edges": N,
  "extend-pick-entity": N,

  // ── FILLET ────────────────────────────────────────────────────────────────
  // Fillet radius is entered as a length.
  "fillet-radius": len(),
  "fillet-first-entity": N,
  "fillet-second-entity": N,

  // ── CHAMFER ───────────────────────────────────────────────────────────────
  // Chamfer distances = two length values.
  "chamfer-distances": wh(),
  "chamfer-first": N,
  "chamfer-second": N,

  // ── BREAK ─────────────────────────────────────────────────────────────────
  "break-select": N,
  "break-first-point": N,
  "break-second-point": N,

  // ── JOIN ──────────────────────────────────────────────────────────────────
  "join-select": N,

  // ── EXPLODE ───────────────────────────────────────────────────────────────
  "explode-select": N,

  // ── LENGTHEN ──────────────────────────────────────────────────────────────
  "lengthen-select": N,
  "lengthen-mode": N,
  "lengthen-value": len(),
  "lengthen-pick-end": N,

  // ── GCCONSTRAINT (geometric constraint palette flow) ─────────────────────
  "geom-constraint-pick-first": N,
  "geom-constraint-pick-second": N,
  "geom-constraint-pick-more": N,
  "geom-constraint-pick-reference-line": N,

  // ── CIRCLE 2-Point sub-flow (FIX-2: bug 1) ────────────────────────────────
  // First point: no DI (AutoCAD shows crosshair tracker only).
  // Second point: distance from first endpoint — when the user types a
  // numeric value, AutoCAD interprets it as the *diameter length* (so the
  // radius is value/2). The state machine reads the typed text as a length
  // along the cursor direction; the reducer commits a circle with
  // center = midpoint and radius = distance/2.
  "circle-2p-first-point": N,
  "circle-2p-second-point": len(),

  // ── CIRCLE 3-Point sub-flow (Phase C.2) ───────────────────────────────────
  // Three-click circumscribing circle. AutoCAD shows distance/length to
  // the previous point at each subsequent click — match the LINE next-point
  // semantics by using `len()` for the secondary clicks.
  "circle-3p-second-point": len(),
  "circle-3p-third-point": len(),

  // ── ARC Start-Center-End sub-flow (Phase C.3) ─────────────────────────────
  // SCE center: no DI (cursor-driven point click).
  // End-angle: subtended angle in degrees (`ang()`).
  "arc-sce-center": N,
  "arc-sce-end-angle": ang(),

  // ── PLINE Width sub-flow (Phase C.1) ──────────────────────────────────────
  // Both widths are entered as length values (matches AutoCAD's "Specify
  // starting width" / "Specify ending width" sequence after typing W).
  "polyline-width-start": len(),
  "polyline-width-end": len(),

  // ── RECTANGLE Dimensions sub-flow (Phase C.4) ─────────────────────────────
  // Length & width are typed lengths; placement click has no DI.
  "rectangle-d-length": len(),
  "rectangle-d-width": len(),
  "rectangle-d-place": N,

  // ── ROTATE Reference sub-flow (Phase C.5) ─────────────────────────────────
  // Reference start/end are point picks (no DI). New-angle is `rot()` —
  // typed in absolute degrees.
  "rotate-ref-start": N,
  "rotate-ref-end": N,
  "rotate-ref-new-angle": rot(),

  // ── DONUT (Phase D3) ──────────────────────────────────────────────────────
  // Inner / outer diameters are typed scalar values; AutoCAD's DONUT
  // command exposes both as plain numeric prompts (no length-along-cursor
  // semantics — they're absolute object dimensions). The center step is
  // a simple click loop with no DI overlay.
  "donut-inner-d": dia(),
  "donut-outer-d": dia(),
  "donut-center": N,

  // ── POLYGON (Phase D2) ────────────────────────────────────────────────────
  // Sides count is a plain integer typed in a length-style field (AutoCAD
  // shows a single numeric DI cell). Mode prompt is keyword-only (no DI).
  // Centre is a click without DI; radius is `rad()` — typed value OR
  // cursor-driven length to the first vertex / edge midpoint.
  "polygon-sides-input": len(),
  "polygon-mode-prompt": N,
  "polygon-center": N,
  "polygon-radius": rad(),

  // ── SPLINE (Phase H first-pass) ───────────────────────────────────────────
  // Fit-points authoring shows distance to the previous fit-point at each
  // subsequent click — like LINE/PLINE next-point. The first click has no
  // reference yet so we render the cursor coordinates only (no DI field).
  "spline-fit-first": N,
  "spline-fit-next": len(),

  // ── Block Authoring — Linear parameter placement (Phase K.2a) ─────────────
  // 3-click flow: base point → end point → label position. Base/end pair is
  // analogous to LINE first/next: distance from base shown at the end click.
  // Label position is a free pick — no DI field (AutoCAD shows the label
  // glyph at the cursor only).
  "param-linear-base": N,
  "param-linear-end": len(),
  "param-linear-label": N,

  // ── Block Authoring — remaining 9 parameter placements (Phase K.2b) ───────
  // Single-click placements just drop the glyph; no DI field.
  "param-point-place": N,
  "param-basepoint-place": N,
  "param-visibility-place": N,
  "param-lookup-place": N,
  // Multi-click placements share Linear's UI: distance shown at the
  // axis-end click; free pick for the label position. Polar's end click
  // is naturally radial → distance + angle is helpful. XY's end click
  // is two-axis → width-height. Rotation's radius end is a length.
  // Alignment's end defines the default-angle direction → rotation field
  // (degrees from base).
  "param-flip-base": N,
  "param-flip-end": len(),
  "param-flip-label": N,
  "param-polar-base": N,
  "param-polar-end": distAng(),
  "param-polar-label": N,
  "param-xy-base": N,
  "param-xy-end": wh(),
  "param-xy-label": N,
  "param-rotation-base": N,
  "param-rotation-radius-end": len(),
  "param-rotation-label": N,
  "param-alignment-base": N,
  "param-alignment-end": rot(),

  // ── Block Authoring — action placement (Phase K.3) ───────────────────────
  // None of the action placement steps render a Dimension Input field —
  // every click in this flow is either an entity / parameter pick or a
  // free-form glyph drop. Crossing-frame corners 1/2 are also free
  // picks (AutoCAD shows a rubber-band crossing window only).
  "action-move-pick-parameter": N,
  "action-move-select-objects": N,
  "action-move-place": N,
  "action-scale-pick-parameter": N,
  "action-scale-select-objects": N,
  "action-scale-place": N,
  "action-stretch-pick-parameter": N,
  "action-stretch-select-objects": N,
  "action-stretch-frame-corner-1": N,
  "action-stretch-frame-corner-2": N,
  "action-stretch-place": N,
  "action-polar-stretch-pick-parameter": N,
  "action-polar-stretch-select-objects": N,
  "action-polar-stretch-frame-corner-1": N,
  "action-polar-stretch-frame-corner-2": N,
  "action-polar-stretch-place": N,
  "action-rotation-pick-parameter": N,
  "action-rotation-select-objects": N,
  "action-rotation-place": N,
  "action-flip-pick-parameter": N,
  "action-flip-select-objects": N,
  "action-flip-place": N,
  "action-array-pick-parameter": N,
  "action-array-select-objects": N,
  "action-array-place": N,
  "action-lookup-pick-parameter": N,
  "action-lookup-select-objects": N,
  "action-lookup-place": N,
} satisfies Record<DraftStep, DimFieldSpec>;

// ─── Lookup helper ────────────────────────────────────────────────────────────

/**
 * Returns the DimFieldSpec for the given step.
 * Falls back to DIM_FIELD_NONE for undefined/null (safe call from overlay).
 */
export function getFieldSpecForStep(
  step: DraftStep | undefined | null,
): DimFieldSpec {
  if (!step) return DIM_FIELD_NONE;
  return DRAFT_STEP_FIELDS[step] ?? DIM_FIELD_NONE;
}
